import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@material-ui/core";
import { Link } from "@backstage/core-components";
import HomeIcon from "@material-ui/icons/Home";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import SearchIcon from "@material-ui/icons/Search";
import SettingsIcon from "@material-ui/icons/Settings";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ViewKanbanIcon from "@material-ui/icons/ViewColumn";
import NotificationsIcon from "@material-ui/icons/Notifications";
import EventNoteIcon from "@material-ui/icons/EventNote";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import { useRootStyles } from "../styles/useRootStyles";
import {
  AgentPoliciesNavItem,
  ForgeNavItem,
  PermissionNavItem,
  StanNavItem,
  CrmlyNavItem,
} from "./NavItems";
import {
  apiDocsAccessPermission,
  docsAccessPermission,
  createAccessPermission,
  resumeGeneratorAccessPermission,
} from "../../../permissions";
import { circlesAccessPermission } from "@internal/plugin-circles";
import { compensationForgeAccessPermission } from "@internal/plugin-compensation-forge";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import ExtensionIcon from "@material-ui/icons/Extension";
import LibraryBooks from "@material-ui/icons/LibraryBooks";
import CreateComponentIcon from "@material-ui/icons/AddCircleOutline";
import DescriptionIcon from "@material-ui/icons/Description";
import { AppContext } from "../hooks/useAppContext";
import { NAV_COLORS } from "../styles/useRootStyles";
import { usePermission } from "@backstage/plugin-permission-react";
import { crmonCommissionsPermission } from "@internal/plugin-crmon";

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  stanDisplayName: string;
  onSearchOpen: () => void;
  appContext: AppContext;
}

export const MobileDrawer = ({
  open,
  onClose,
  stanDisplayName,
  onSearchOpen,
  appContext,
}: MobileDrawerProps) => {
  const classes = useRootStyles();
  const { allowed: commissionsAllowed } = usePermission({
    permission: crmonCommissionsPermission,
  });

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
      // eslint-disable-next-line security/detect-object-injection
      PaperProps={{ style: { backgroundColor: NAV_COLORS[appContext] } }}
    >
      {appContext === "crmon" ? (
        <List className={classes.drawerList} style={{ paddingTop: 0 }}>
          {/* CRMon mascot header */}
          <ListItem disableGutters style={{ padding: 0 }}>
            <div
              className={classes.crmonDrawerHeader}
              style={{ width: "100%" }}
            >
              <img
                src="/leapstaq-crmon-mascot.png"
                alt="CRMon"
                className={classes.crmonDrawerLogo}
              />
              <Typography className={classes.crmonDrawerTitle}>
                Crmly
              </Typography>
            </div>
          </ListItem>

          <ListItem
            button
            component={Link}
            to="/crmon"
            className={classes.drawerItem}
            onClick={onClose}
          >
            <ListItemIcon className={classes.drawerIcon}>
              <ViewKanbanIcon />
            </ListItemIcon>
            <ListItemText primary="Pipeline" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to="/crmon/nudges"
            className={classes.drawerItem}
            onClick={onClose}
          >
            <ListItemIcon className={classes.drawerIcon}>
              <NotificationsIcon />
            </ListItemIcon>
            <ListItemText primary="Nudges" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to="/crmon/weekly-sync"
            className={classes.drawerItem}
            onClick={onClose}
          >
            <ListItemIcon className={classes.drawerIcon}>
              <EventNoteIcon />
            </ListItemIcon>
            <ListItemText primary="Weekly Sync" />
          </ListItem>
          {commissionsAllowed && (
            <ListItem
              button
              component={Link}
              to="/crmon/commissions"
              className={classes.drawerItem}
              onClick={onClose}
            >
              <ListItemIcon className={classes.drawerIcon}>
                <AttachMoneyIcon />
              </ListItemIcon>
              <ListItemText primary="Commissions" />
            </ListItem>
          )}

          <ListItem
            button
            className={classes.drawerItem}
            onClick={() => {
              onClose();
              onSearchOpen();
            }}
          >
            <ListItemIcon className={classes.drawerIcon}>
              <SearchIcon />
            </ListItemIcon>
            <ListItemText primary="Search" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to="/settings"
            className={classes.drawerItem}
            onClick={onClose}
          >
            <ListItemIcon className={classes.drawerIcon}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to="/"
            className={classes.drawerItem}
            onClick={onClose}
            style={{ opacity: 0.65, marginTop: 8 }}
          >
            <ListItemIcon className={classes.drawerIcon}>
              <ArrowBackIcon />
            </ListItemIcon>
            <ListItemText primary="Back to Portal" />
          </ListItem>
        </List>
      ) : (
        <List className={classes.drawerList}>
          <ListItem
            button
            component={Link}
            to="/"
            className={classes.drawerItem}
            onClick={onClose}
          >
            <ListItemIcon className={classes.drawerIcon}>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText primary="Home" />
          </ListItem>
          <ForgeNavItem mobile onClick={onClose} />
          <PermissionNavItem
            mobile
            to="circles"
            icon={AccountTreeIcon}
            label="Circles"
            permission={circlesAccessPermission}
            onClick={onClose}
          />
          <PermissionNavItem
            mobile
            to="api-docs"
            icon={ExtensionIcon}
            label="APIs"
            permission={apiDocsAccessPermission}
            onClick={onClose}
          />
          <PermissionNavItem
            mobile
            to="docs"
            icon={LibraryBooks}
            label="Docs"
            permission={docsAccessPermission}
            onClick={onClose}
          />
          <PermissionNavItem
            mobile
            to="create"
            icon={CreateComponentIcon}
            label="Create"
            permission={createAccessPermission}
            onClick={onClose}
          />
          <ListItem
            button
            component={Link}
            to="odoo-timesheet"
            className={classes.drawerItem}
            onClick={onClose}
          >
            <ListItemIcon className={classes.drawerIcon}>
              <AccessTimeIcon />
            </ListItemIcon>
            <ListItemText primary="Timewriting" />
          </ListItem>
          <PermissionNavItem
            mobile
            to="resume-generator"
            icon={DescriptionIcon}
            label="Resumes"
            permission={resumeGeneratorAccessPermission}
            onClick={onClose}
          />
          <PermissionNavItem
            mobile
            to="compensation-forge"
            icon={AttachMoneyIcon}
            label="Compensation Forge"
            permission={compensationForgeAccessPermission}
            onClick={onClose}
          />
          <AgentPoliciesNavItem mobile onClick={onClose} />
          <StanNavItem mobile onClick={onClose} displayName={stanDisplayName} />
          <CrmlyNavItem mobile onClick={onClose} />
          <ListItem
            button
            className={classes.drawerItem}
            onClick={() => {
              onClose();
              onSearchOpen();
            }}
          >
            <ListItemIcon className={classes.drawerIcon}>
              <SearchIcon />
            </ListItemIcon>
            <ListItemText primary="Search" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to="/settings"
            className={classes.drawerItem}
            onClick={onClose}
          >
            <ListItemIcon className={classes.drawerIcon}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItem>
        </List>
      )}
    </Drawer>
  );
};
